/**
 * batch-62 uitrusting-inkoop (FO-466 + FO-467) - pure regels voor het koppelen
 * van pakbon-/factuurregels aan de uitrusting van een medewerker, en voor het
 * verwerken zonder bestelling (telverschil, afwikkeling, opschoonlijst).
 *
 * Geen DB en geen I/O: de API laadt de kandidaten en uitgiftes en roept deze
 * functies aan, zodat elke beslissing los te toetsen is.
 *
 * Harde regels (spec 166/167 + CLAUDE.md):
 *  - maten altijd canoniek via `maatMatcht` mét maatType, nooit ===;
 *  - SKU's via `normaliseerSku` (hoofdletters, witruimte en streepjes weg);
 *  - naam+maat via dezelfde `artikelnaamOverlap` >= 0,66 + Jaccard-tiebreak +
 *    generieke-naam-guard als de catalogus-inhaalslag (FO-419), niet opnieuw
 *    bedacht;
 *  - twijfel = geen voorstel (review met kandidaten), nooit gokken;
 *  - dubbel bezit ontstaat nooit per ongeluk: een treffer binnen het venster
 *    stelt "alleen koppelen" voor.
 */
import { utcToAmsterdamWallclock } from './amsterdam-tijd'
import { isMaatloos, maatMatcht } from './uitrusting-maat'
import {
  artikelnaamJaccard,
  artikelnaamOverlap,
  normaliseerArtikelnaamVoorMatch,
} from './uitrusting-variant-voorraad'
import { normaliseerSku } from './catalogus-voorraad-inhaal'

// ─── Dubbel-controle-venster ────────────────────────────────────────────────

export interface DubbelVenster {
  /** Dagen vóór de leverdatum die nog als "dezelfde levering" tellen. */
  dagenVoor: number
  /** Dagen ná de leverdatum. */
  dagenNa: number
}

/** Spec 166 A1: 14 dagen vóór t/m 7 dagen ná de factuur-/leverdatum. */
export const DUBBEL_VENSTER_STANDAARD: DubbelVenster = { dagenVoor: 14, dagenNa: 7 }

/** NL-kalenderdag als dagnummer sinds epoch (DST-veilig, los van de host-tz). */
function nlDagNummer(d: Date): number {
  const [jaar, maand, dag] = utcToAmsterdamWallclock(d).ymd.split('-').map(Number)
  return Math.floor(Date.UTC(jaar!, maand! - 1, dag!) / 86_400_000)
}

/** Aantal NL-kalenderdagen van `van` naar `tot` (`tot` later = positief). */
export function nlKalenderdagenTussen(van: Date, tot: Date): number {
  return nlDagNummer(tot) - nlDagNummer(van)
}

/**
 * Valt `uitgifteDatum` binnen het venster rond `ankerDatum`? Randen tellen mee
 * en er wordt in NL-kalenderdagen gerekend: een uitgifte om 00:30 NL-tijd hoort
 * bij die NL-dag, ook als het in UTC nog de vorige dag was.
 */
export function binnenDubbelVenster(
  uitgifteDatum: Date,
  ankerDatum: Date,
  venster: DubbelVenster = DUBBEL_VENSTER_STANDAARD,
): boolean {
  const verschil = nlKalenderdagenTussen(ankerDatum, uitgifteDatum)
  return verschil >= -venster.dagenVoor && verschil <= venster.dagenNa
}

// ─── Maat ───────────────────────────────────────────────────────────────────

function isLeegOfMaatloos(maat: string | null | undefined): boolean {
  return maat == null || maat.trim().length === 0 || isMaatloos(maat)
}

/**
 * Maat-gelijkheid voor koppelen en dubbel-controle: maatloos matcht alleen
 * maatloos, anders canoniek via `maatMatcht` (C52 = 52, XXL = 2XL, 44/45 dekt
 * 44 én 45).
 */
export function zelfdeUitrustingMaat(
  a: string | null | undefined,
  b: string | null | undefined,
  maatType: string | null | undefined,
): boolean {
  const aLoos = isLeegOfMaatloos(a)
  const bLoos = isLeegOfMaatloos(b)
  if (aLoos || bLoos) return aLoos && bLoos
  return maatMatcht(a, b, maatType)
}

/**
 * Wijkt de maat op de pakbon-/factuurregel af van de geregistreerde maat van de
 * medewerker? Waarschuwing (mogelijk verkeerde persoon), nooit een blokkade.
 * Onbekend aan één van beide kanten = geen afwijking.
 */
export function uitrustingMaatWijktAf(
  regelMaat: string | null | undefined,
  geregistreerdeMaat: string | null | undefined,
  maatType: string | null | undefined,
): boolean {
  if (isLeegOfMaatloos(regelMaat) || isLeegOfMaatloos(geregistreerdeMaat)) return false
  return !maatMatcht(geregistreerdeMaat, regelMaat, maatType)
}

// ─── Dubbel-controle ────────────────────────────────────────────────────────

export type UitgifteKandidaatSoort =
  | 'BESTELREGEL'
  | 'UITRUSTING_ITEM'
  | 'VOORRAAD_MUTATIE'
  | 'UNIEK_ITEM'
  | 'KOPPELING'

/** Eén bestaande uitgifte op de medewerker, genormaliseerd naar catalogus-artikel + maat. */
export interface UitgifteKandidaat {
  soort: UitgifteKandidaatSoort
  id: string
  uitrustingArtikelId: string
  maat: string | null
  datum: Date
  aantal: number
}

export interface DubbelArtikel {
  /** Catalogus-artikel (UitrustingArtikel.id). */
  id: string
  maatType: string | null
}

/**
 * FO-466 §B.5 - staat hetzelfde catalogus-artikel + maat al op de medewerker
 * met een uitgiftedatum binnen het venster rond `datum`? Levert de treffers,
 * dichtstbijzijnde eerst; leeg = geen dubbel.
 */
export function vindMogelijkDubbel<T extends UitgifteKandidaat>(
  uitgiftes: readonly T[],
  artikel: DubbelArtikel,
  maat: string | null,
  datum: Date,
  venster: DubbelVenster = DUBBEL_VENSTER_STANDAARD,
): T[] {
  return uitgiftes
    .filter((u) => u.uitrustingArtikelId === artikel.id)
    .filter((u) => zelfdeUitrustingMaat(u.maat, maat, artikel.maatType))
    .filter((u) => binnenDubbelVenster(u.datum, datum, venster))
    .sort(
      (a, b) =>
        Math.abs(nlKalenderdagenTussen(datum, a.datum)) -
        Math.abs(nlKalenderdagenTussen(datum, b.datum)),
    )
}

// ─── Artikel-match (SKU, anders naam + maat) ────────────────────────────────

export type UitrustingCategorieSleutel = 'KLEDING' | 'GEREEDSCHAP' | 'PBM' | 'VEILIGHEID'

/** Een catalogus-variant (maat) zoals de matcher hem nodig heeft. */
export interface CatalogusVariantKandidaat {
  variantId: string
  artikelId: string
  artikelNaam: string
  /** UitrustingVariant.artikelnummer: uniek per maat, vaak het leveranciersnummer. */
  artikelnummer: string
  /** UitrustingArtikel.leverancierArtNr (artikel-niveau, zonder maat). */
  leverancierArtNr: string | null
  /** Artikelnummer, leverancierArtNr en aliassen van het gekoppelde voorraad-artikel. */
  voorraadSkus: readonly string[]
  maat: string | null
  maatType: string | null
  categorie: UitrustingCategorieSleutel
  actief: boolean
}

export type ArtikelMatchStatus = 'EENDUIDIG' | 'MEERDERE' | 'GEEN'
export type ArtikelMatchGrond = 'SKU' | 'NAAM'

export interface ArtikelMatchResultaat {
  status: ArtikelMatchStatus
  grond: ArtikelMatchGrond | null
  variant: CatalogusVariantKandidaat | null
  /** De SKU wijst een variant aan waarvan de maat botst met de regel-maat. */
  maatBotst: boolean
  /** Bij MEERDERE of GEEN: de beste kandidaten voor de review (max 5). */
  suggesties: CatalogusVariantKandidaat[]
}

/** Zelfde drempel als de catalogus-inhaalslag (FO-419). */
export const ARTIKEL_NAAM_OVERLAP_DREMPEL = 0.66
const MAX_SUGGESTIES = 5

function isGeneriekeNaam(naam: string): boolean {
  return normaliseerArtikelnaamVoorMatch(naam).length < 2
}

function resultaat(
  status: ArtikelMatchStatus,
  grond: ArtikelMatchGrond | null,
  variant: CatalogusVariantKandidaat | null,
  suggesties: CatalogusVariantKandidaat[] = [],
  maatBotst = false,
): ArtikelMatchResultaat {
  return { status, grond, variant, maatBotst, suggesties: suggesties.slice(0, MAX_SUGGESTIES) }
}

/** Beste naam-suggesties, ongeacht maat (voor "niet herkend"). */
function naamSuggesties(
  omschrijving: string,
  kandidaten: readonly CatalogusVariantKandidaat[],
): CatalogusVariantKandidaat[] {
  return kandidaten
    .filter((k) => k.actief)
    .map((k) => ({ k, score: artikelnaamJaccard(omschrijving, k.artikelNaam) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.k)
}

/**
 * Artikel-match voor één pakbon-/factuurregel:
 *  1. SKU op het variant-artikelnummer (uniek per maat): exact = eenduidig.
 *  2. SKU op het artikel-leveranciersnummer of het gekoppelde voorraad-artikel,
 *     versmald op de regel-maat.
 *  3. Genormaliseerde naam (overlap >= 0,66) + maat (canoniek); bij meerdere
 *     artikelen wint de hoogste Jaccard-score, een gelijkspel of een generieke
 *     naam ("Werkbroek") is twijfel.
 * Geen eenduidige match -> review met suggesties (en de mogelijkheid het
 * artikel aan te maken).
 */
export function matchUitrustingArtikel(
  regel: { sku: string | null; omschrijving: string; maat: string | null },
  kandidaten: readonly CatalogusVariantKandidaat[],
): ArtikelMatchResultaat {
  const sku = normaliseerSku(regel.sku)

  if (sku.length > 0) {
    // 1. Variant-artikelnummer: dat is per definitie één maat.
    const opVariant = kandidaten.filter((k) => normaliseerSku(k.artikelnummer) === sku)
    if (opVariant.length === 1) {
      const variant = opVariant[0]!
      const botst =
        !isLeegOfMaatloos(regel.maat) &&
        !isLeegOfMaatloos(variant.maat) &&
        !zelfdeUitrustingMaat(variant.maat, regel.maat, variant.maatType)
      return resultaat('EENDUIDIG', 'SKU', variant, [], botst)
    }
    if (opVariant.length > 1) return resultaat('MEERDERE', 'SKU', null, opVariant)

    // 2. Artikel-niveau of voorraad-artikel: versmallen op maat.
    const opArtikel = kandidaten.filter(
      (k) =>
        normaliseerSku(k.leverancierArtNr) === sku ||
        k.voorraadSkus.some((s) => normaliseerSku(s) === sku),
    )
    if (opArtikel.length > 0) {
      const maatPast = opArtikel.filter((k) =>
        zelfdeUitrustingMaat(k.maat, regel.maat, k.maatType),
      )
      const actief = maatPast.filter((k) => k.actief)
      const pool = actief.length > 0 ? actief : maatPast
      if (pool.length === 1) return resultaat('EENDUIDIG', 'SKU', pool[0]!)
      return resultaat('MEERDERE', 'SKU', null, pool.length > 0 ? pool : opArtikel)
    }
  }

  // 3. Naam + maat.
  const naamKandidaten = kandidaten.filter(
    (k) =>
      k.actief &&
      artikelnaamOverlap(regel.omschrijving, k.artikelNaam) >= ARTIKEL_NAAM_OVERLAP_DREMPEL &&
      zelfdeUitrustingMaat(k.maat, regel.maat, k.maatType),
  )
  if (naamKandidaten.length === 0) {
    return resultaat('GEEN', null, null, naamSuggesties(regel.omschrijving, kandidaten))
  }

  // Per artikel de hoogste Jaccard-score; een gelijkspel tussen artikelen is twijfel.
  const scores = naamKandidaten.map((k) => ({
    k,
    score: artikelnaamJaccard(regel.omschrijving, k.artikelNaam),
  }))
  const hoogste = Math.max(...scores.map((s) => s.score))
  const winnaars = scores.filter((s) => Math.abs(s.score - hoogste) < 1e-9).map((s) => s.k)
  const winnendeArtikelen = new Set(winnaars.map((k) => k.artikelId))
  if (winnendeArtikelen.size !== 1 || winnaars.length !== 1) {
    return resultaat('MEERDERE', 'NAAM', null, winnaars.length > 1 ? winnaars : naamKandidaten)
  }
  const winnaar = winnaars[0]!
  if (isGeneriekeNaam(winnaar.artikelNaam)) {
    return resultaat('MEERDERE', 'NAAM', null, [winnaar])
  }
  return resultaat('EENDUIDIG', 'NAAM', winnaar)
}

// ─── Voorstel-actie per regel ───────────────────────────────────────────────

export type UitrustingKoppelActieSleutel =
  | 'UITGIFTE'
  | 'LEVERING_BESTELLING'
  | 'ALLEEN_KOPPELEN'
  | 'NAAR_VOORRAAD'
  | 'OVERGESLAGEN'
  | 'RETOUR'

/** Een negatief aantal of bedrag = creditregel/retour. */
export function isUitrustingCreditregel(regel: {
  aantal: number
  bedrag: number | null
}): boolean {
  return regel.aantal < 0 || (regel.bedrag != null && regel.bedrag < 0)
}

/**
 * Regels die geen stuk uitrusting zijn (verzendkosten, bedrukking, korting,
 * toeslagen). Zonder persoon en zonder artikel-match stellen we "overslaan"
 * voor, zodat ze niet eindeloos in de werkvoorraad blijven staan.
 */
const NIET_ARTIKEL_PATROON =
  /\b(verzend\w*|vracht\w*|porto|transport\w*|bezorg\w*|bedrukk\w*|borduur\w*|logo|korting|toeslag\w*|administratie\w*|orderkosten|kleinorder\w*|emballage|statiegeld)\b/i

export function isNietArtikelRegel(omschrijving: string): boolean {
  return NIET_ARTIKEL_PATROON.test(omschrijving)
}

export interface VoorstelInput {
  isCreditregel: boolean
  /** Geen stuk uitrusting (zie `isNietArtikelRegel`). */
  nietArtikel?: boolean
  heeftPersoonsnaam: boolean
  medewerkerEenduidig: boolean
  artikelEenduidig: boolean
  /** Heeft het gematchte artikel een voorraad-bron (voor "naar voorraad")? */
  heeftVoorraadBron: boolean
  aantalOpenBestelregels: number
  aantalDubbelTreffers: number
  aantalRetourKandidaten: number
}

/**
 * De voorgestelde actie, of null wanneer een mens moet kiezen (twijfel over
 * persoon of artikel). Volgorde: creditregel -> geen persoon -> twijfel ->
 * lopende bestelling -> dubbel (default alleen koppelen) -> nieuwe uitgifte.
 */
export function bepaalVoorgesteldeActie(i: VoorstelInput): UitrustingKoppelActieSleutel | null {
  if (i.isCreditregel) return i.aantalRetourKandidaten > 0 ? 'RETOUR' : null
  if (!i.heeftPersoonsnaam) {
    if (i.artikelEenduidig && i.heeftVoorraadBron) return 'NAAR_VOORRAAD'
    return !i.artikelEenduidig && i.nietArtikel ? 'OVERGESLAGEN' : null
  }
  if (!i.medewerkerEenduidig || !i.artikelEenduidig) return null
  if (i.aantalOpenBestelregels > 0) return 'LEVERING_BESTELLING'
  if (i.aantalDubbelTreffers > 0) return 'ALLEEN_KOPPELEN'
  return 'UITGIFTE'
}

// ─── FO-467: telverschil + afwikkeling ──────────────────────────────────────

/** "uitgegeven zonder registratie - telverschil {n} stuks" (spec 167 §8). */
export function voorraadVerschilOmschrijving(aantal: number): string {
  return `uitgegeven zonder registratie - telverschil ${aantal} ${aantal === 1 ? 'stuk' : 'stuks'}`
}

export interface OpenVerschilRef {
  id: string
  aantal: number
  createdAt: Date
}

/**
 * Welke open verschillen (oudste eerst) passen binnen de beschikbare stand bij
 * een ontvangstregistratie? Afwikkelen boekt ze alsnog af; wat niet past blijft
 * open. De stand komt zo nooit onder nul.
 */
export function kiesAfwikkelbareVerschillen<T extends OpenVerschilRef>(
  verschillen: readonly T[],
  beschikbaar: number,
): { passend: T[]; nietPassend: T[] } {
  const passend: T[] = []
  const nietPassend: T[] = []
  let rest = Math.max(0, beschikbaar)
  const oudsteEerst = [...verschillen].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
  for (const v of oudsteEerst) {
    if (v.aantal > 0 && v.aantal <= rest) {
      passend.push(v)
      rest -= v.aantal
    } else {
      nietPassend.push(v)
    }
  }
  return { passend, nietPassend }
}

// ─── FO-467: opschoonlijst ──────────────────────────────────────────────────

export type OpschoonVoorstelSleutel = 'VERWERKEN_ZONDER_BESTELLING' | 'SLUITEN_ALS_DUBBEL'

/**
 * Een open aanvraag waarvan de levering bewezen is door bestaand bezit:
 *  - is dat bezit de levering van een ÁNDERE bestelling, dan is deze aanvraag
 *    een dubbele (die andere bestelling heeft geleverd), ongeacht credits;
 *  - droeg het bezit zelf al credits (een Van Ysselt-afname bij een pakbon),
 *    dan is deze aanvraag ook een dubbele en gaan zijn credits terug;
 *  - anders (uitgifte uit voorraad, los dossier-item, pakbon zonder credits) is
 *    dit de bestelling bij die levering en blijven de credits staan.
 */
export function bepaalOpschoonVoorstel(bewijs: {
  isBestelling: boolean
  draagtCredits: boolean
}): OpschoonVoorstelSleutel {
  return bewijs.isBestelling || bewijs.draagtCredits ? 'SLUITEN_ALS_DUBBEL' : 'VERWERKEN_ZONDER_BESTELLING'
}

/**
 * Welke regels van één bestelling mogen in de bulk-opschoning mee? Een
 * GOEDGEKEURDE bestelling alleen als ALLE actieve regels bewezen geleverd zijn
 * (anders zou de rest ongemerkt blijven liggen); bij een VERWERKTE bestelling
 * per regel. De rest blijft handmatig (spec 167 §5.5).
 */
export function opschoonbareRegels(
  bestellingStatus: 'GOEDGEKEURD' | 'VERWERKT',
  regels: ReadonlyArray<{ regelId: string; bewezen: boolean }>,
): { opschoonbaar: string[]; blokkade: string | null } {
  const bewezen = regels.filter((r) => r.bewezen).map((r) => r.regelId)
  if (bestellingStatus === 'VERWERKT') {
    return { opschoonbaar: bewezen, blokkade: null }
  }
  if (bewezen.length === regels.length && regels.length > 0) {
    return { opschoonbaar: bewezen, blokkade: null }
  }
  return {
    opschoonbaar: [],
    blokkade:
      'Niet alle regels zijn aantoonbaar geleverd: verwerk deze bestelling handmatig op het verwerkscherm.',
  }
}
